/**
 * ModelValidityBadge - Displays model validity status based on AUC-ROC threshold
 * Shows whether a model meets quality threshold (AUC-ROC >= 0.6)
 */

import clsx from 'clsx'

interface ModelValidityBadgeProps {
  isValid: boolean
  aucRoc?: number
  showScore?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const sizes = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm'
}

export default function ModelValidityBadge({
  isValid,
  aucRoc,
  showScore = false,
  size = 'md',
  className
}: ModelValidityBadgeProps) {
  const config = isValid
    ? {
        bg: 'bg-status-healthy/20',
        text: 'text-status-healthy',
        icon: '✓',
        label: 'Valid Model'
      }
    : {
        bg: 'bg-status-warning/20',
        text: 'text-status-warning',
        icon: '⚠',
        label: 'Low Quality'
      }

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-md font-medium',
        config.bg,
        config.text,
        sizes[size],
        className
      )}
      title={
        isValid
          ? `Model meets quality threshold (AUC-ROC >= 0.6)${aucRoc ? `: ${(aucRoc * 100).toFixed(1)}%` : ''}`
          : `Model below quality threshold (AUC-ROC < 0.6)${aucRoc ? `: ${(aucRoc * 100).toFixed(1)}%` : ''}`
      }
    >
      <span className="text-[10px]">{config.icon}</span>
      {showScore && aucRoc !== undefined ? (
        <span>{(aucRoc * 100).toFixed(0)}%</span>
      ) : (
        <span>{config.label}</span>
      )}
    </span>
  )
}

// Compact version for tables
export function ModelValidityDot({
  isValid,
  aucRoc,
  className
}: {
  isValid: boolean
  aucRoc?: number
  className?: string
}) {
  return (
    <span
      className={clsx(
        'inline-block w-2 h-2 rounded-full',
        isValid ? 'bg-status-healthy' : 'bg-status-warning',
        className
      )}
      title={
        isValid
          ? `Valid Model${aucRoc ? ` (AUC-ROC: ${(aucRoc * 100).toFixed(1)}%)` : ''}`
          : `Low Quality Model${aucRoc ? ` (AUC-ROC: ${(aucRoc * 100).toFixed(1)}%)` : ''}`
      }
    />
  )
}
